import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Button, Table, Tag, Space, message, Alert, Modal, Tooltip, Badge,
  Progress, Statistic, Row, Col
} from 'antd';
import {
  SendOutlined, CopyOutlined, LinkOutlined, MessageOutlined
} from '@ant-design/icons';
import axios from 'axios';
import AuthService from '../../utils/auth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';
const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || window.location.origin;

const StepPublishSend = ({ tourists, tour, tourNotice, completeness, onRefresh }) => {
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedTouristIds, setSelectedTouristIds] = useState([]);
  const [sendResult, setSendResult] = useState(null);
  const [batchProgress, setBatchProgress] = useState(null); // {jobId,current,total,currentName,sentCount,failedCount,skippedCount,status}
  const [smsSending, setSmsSending] = useState(false);
  const [smsResult, setSmsResult] = useState(null); // {sent,failed,skipped,results:[{phone,touristNames,status,reason}]}
  const pollTimerRef = useRef(null);

  // 组件卸载时清掉轮询定时器
  useEffect(() => () => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
  }, []);

  const touristDetails = completeness?.tourists || [];

  // 进入页面时自动生成链接
  useEffect(() => {
    if (tour?.id) {
      generateLinks();
    }
  }, [tour?.id]);

  const generateLinks = async () => {
    try {
      setGenerating(true);
      await axios.post(
        `${API_BASE}/tour-notices/tour/${tour.id}/generate-all-links`,
        {},
        { headers: AuthService.getAuthHeaders() }
      );
      onRefresh();
    } catch (err) {
      console.error('生成链接失败:', err);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyLink = (noticeLink) => {
    const url = `${FRONTEND_URL}/notice/${noticeLink}`;
    navigator.clipboard.writeText(url).then(() => {
      message.success('链接已复制');
    }).catch(() => {
      message.error('复制失败');
    });
  };

  // 短信由会员系统按号码发送：同一号码在本团只发一条，force 时已发送过的也重发
  // 返回会员端结果；silent 时不弹结果框（与邮件一起发送时汇总到结果栏）
  const sendSms = async (touristIds, force, { silent = false } = {}) => {
    setSmsSending(true);
    try {
      const res = await axios.post(
        `${API_BASE}/tour-notices/tour/${tour.id}/send-sms`,
        { touristIds, force },
        { headers: AuthService.getAuthHeaders(), timeout: 90000 }
      );
      const data = res.data?.data;
      if (!data) throw new Error('短信服务未返回结果');
      if (!silent) {
        setSmsResult(data);
        if (data.failed > 0) {
          message.error(`短信发送失败 ${data.failed} 条`);
        } else if (data.sent > 0) {
          message.success(`短信已发送 ${data.sent} 条`);
        } else {
          message.warning('未发送短信');
        }
      }
      return data;
    } catch (err) {
      const msg = err.response?.data?.message || `短信发送失败：${err.message}`;
      if (!silent) message.error(msg);
      return { error: msg, sent: 0, failed: 0, skipped: 0, results: [] };
    } finally {
      setSmsSending(false);
    }
  };

  const smsSummary = (sms) => {
    if (!sms) return '短信未发送';
    if (sms.error) return sms.error;
    return `短信：成功 ${sms.sent}，失败 ${sms.failed}，跳过 ${sms.skipped}`;
  };

  // 单个游客：短信 + 邮件
  const sendOne = async (record, resend) => {
    setSending(true);
    try {
      const hasPhone = !!(record.contactPhone || record.phone);
      const sms = hasPhone ? await sendSms([record.id], resend && record.smsSent, { silent: true }) : null;

      let emailText = '邮件未发送：销售无邮箱';
      let emailOk = false;
      if (record.hasSalespersonEmail) {
        try {
          const res = await axios.post(
            `${API_BASE}/tour-notices/tour/${tour.id}/batch-send-emails`,
            { touristIds: [record.id] },
            { headers: AuthService.getAuthHeaders() }
          );
          const data = res.data?.data;
          emailOk = data?.sentCount > 0;
          emailText = emailOk
            ? '邮件已发送'
            : '邮件发送失败：' + (data?.details?.failed?.[0]?.error || data?.details?.skipped?.[0]?.reason || '未知原因');
        } catch (err) {
          emailText = '邮件发送失败：' + (err.response?.data?.message || err.message);
        }
      }

      const smsText = hasPhone ? smsSummary(sms) : '短信未发送：无联系电话';
      const smsOk = sms && !sms.error && sms.failed === 0 && sms.sent > 0;
      const text = `${emailText}；${smsText}`;
      if (emailOk && smsOk) message.success(text);
      else if (emailOk || smsOk) message.warning(text);
      else message.error(text);
      if (sms && !sms.error && sms.results?.length) setSmsResult(sms);
      onRefresh();
    } finally {
      setSending(false);
    }
  };

  const handleSingleSend = (record) => {
    if (record.emailSent || record.smsSent) {
      Modal.confirm({
        title: '确认重新发送',
        content: `${record.passportName || record.touristName || ''} 的出团通知书已发送过，确定重新发送邮件与短信吗？`,
        okText: '重新发送',
        onOk: () => sendOne(record, true),
      });
      return;
    }
    sendOne(record, false);
  };

  const pollEmailJob = (jobId, sms) => {
    pollTimerRef.current = setInterval(async () => {
      try {
        const pollRes = await axios.get(
          `${API_BASE}/tour-notices/batch-jobs/${jobId}`,
          { headers: AuthService.getAuthHeaders() }
        );
        const j = pollRes.data?.data;
        if (!j) return;
        setBatchProgress(prev => ({ ...prev, ...j }));
        if (j.status !== 'running') {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
          setSending(false);
          if (j.status === 'done') {
            message.success(`邮件发送完成：成功 ${j.sentCount}，失败 ${j.failedCount}`);
            setSendResult({
              sentCount: j.sentCount,
              failedCount: j.failedCount,
              skippedCount: j.skippedCount,
              details: j.details || {},
              sms,
            });
            setSelectedTouristIds([]);
            onRefresh();
            setTimeout(() => setBatchProgress(null), 2500); // 自动关闭进度框
          } else if (j.status === 'error') {
            message.error('批量发送异常: ' + (j.error || '未知'));
            setBatchProgress(null);
            setSendResult({ sentCount: 0, failedCount: 0, skippedCount: 0, details: {}, sms });
          }
        }
      } catch (pollErr) {
        console.error('轮询进度失败:', pollErr);
      }
    }, 1500);
  };

  // 批量：先发短信（同步拿逐号码结果），再启动邮件任务
  const handleBatchSend = () => {
    if (selectedTouristIds.length === 0) {
      message.warning('请选择要发送的游客');
      return;
    }

    Modal.confirm({
      title: '确认批量发送',
      content: `将为 ${selectedTouristIds.length} 位游客发送出团通知书邮件与短信。同一号码只发送一条短信，已发送过的号码跳过。`,
      okText: '发送',
      onOk: async () => {
        setSending(true);
        setSendResult(null);
        const ids = [...selectedTouristIds];
        const sms = await sendSms(ids, false, { silent: true });

        let jobId;
        try {
          const res = await axios.post(
            `${API_BASE}/tour-notices/tour/${tour.id}/batch-send-emails?async=1`,
            { touristIds: ids },
            { headers: AuthService.getAuthHeaders() }
          );
          if (!res.data?.success) throw new Error(res.data?.message || '启动任务失败');
          jobId = res.data.data?.jobId;
          if (!jobId) throw new Error('未拿到任务 ID');
          setBatchProgress({
            jobId,
            current: 0,
            total: res.data.data.total || ids.length,
            currentName: '排队中...',
            sentCount: 0,
            failedCount: 0,
            skippedCount: res.data.data.skippedCount || 0,
            status: 'running'
          });
        } catch (err) {
          message.error('邮件发送启动失败: ' + (err.response?.data?.message || err.message));
          setSendResult({ sentCount: 0, failedCount: 0, skippedCount: 0, details: {}, sms, emailError: err.response?.data?.message || err.message });
          setSending(false);
          onRefresh();
          return;
        }
        pollEmailJob(jobId, sms);
      },
    });
  };

  const handleBatchSms = () => {
    if (selectedTouristIds.length === 0) {
      message.warning('请选择要发送短信的游客');
      return;
    }
    Modal.confirm({
      title: '确认仅发送短信',
      content: `将向所选 ${selectedTouristIds.length} 位游客的联系电话发送出团通知书短信，不发送邮件。同一号码只发送一条，已发送过的号码跳过。`,
      okText: '发送',
      onOk: async () => { await sendSms(selectedTouristIds, false); onRefresh(); },
    });
  };

  const rowSelection = {
    selectedRowKeys: selectedTouristIds,
    onChange: (keys) => setSelectedTouristIds(keys),
  };

  const columns = [
    {
      title: '护照姓名',
      key: 'passportName',
      width: 150,
      fixed: 'left',
      render: (_, record) => record.passportName || record.touristName || '-',
    },
    {
      title: '联系人',
      dataIndex: 'contactPerson',
      key: 'contactPerson',
      width: 110,
      render: (text) => text || <span style={{ color: '#999' }}>-</span>,
    },
    {
      title: '联系电话',
      key: 'contactPhone',
      width: 140,
      render: (_, record) => {
        const phone = record.contactPhone || record.phone;
        return phone
          ? <span style={{ whiteSpace: 'nowrap' }}>{phone}</span>
          : <span style={{ color: '#999' }}>-</span>;
      },
    },
    {
      title: '销售',
      dataIndex: 'salespersonName',
      key: 'salespersonName',
      width: 100,
      render: (text) => text || <span style={{ color: '#999' }}>-</span>,
    },
    {
      title: '通知链接',
      key: 'noticeLink',
      width: 150,
      render: (_, record) => {
        if (!record.noticeLink) {
          return <Tag color="warning">未生成</Tag>;
        }
        return (
          <Space>
            <Tag color="success">已生成</Tag>
            <Tooltip title="复制链接">
              <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopyLink(record.noticeLink)} />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: '邮件状态',
      key: 'emailStatus',
      width: 150,
      render: (_, record) => {
        if (!record.emailSent) {
          return <Tag color="default">未发送</Tag>;
        }
        return (
          <Space direction="vertical" size={0}>
            <Tag color="success">已发送 {record.noticeSentCount} 次</Tag>
            {record.noticeSentAt && (
              <span style={{ fontSize: 11, color: '#999' }}>
                {new Date(record.noticeSentAt).toLocaleString('zh-CN')}
              </span>
            )}
          </Space>
        );
      },
    },
    {
      title: '短信状态',
      key: 'smsStatus',
      width: 150,
      render: (_, record) => {
        if (!record.smsSent) {
          return <Tag color="default">未发送</Tag>;
        }
        return (
          <Space direction="vertical" size={0}>
            <Tag color="success">已发送</Tag>
            {record.smsSentAt && (
              <span style={{ fontSize: 11, color: '#999' }}>
                {new Date(record.smsSentAt).toLocaleString('zh-CN')}
              </span>
            )}
          </Space>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 110,
      render: (_, record) => {
        const sentBefore = record.emailSent || record.smsSent;
        return (
          <Button
            size="small"
            type={sentBefore ? 'default' : 'primary'}
            ghost={!sentBefore}
            icon={<SendOutlined />}
            onClick={() => handleSingleSend(record)}
            loading={sending || smsSending}
          >
            {sentBefore ? '重新发送' : '发送'}
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      {/* Send Result */}
      {sendResult && (
        <Alert
          type={sendResult.failedCount > 0 || sendResult.emailError || sendResult.sms?.error || sendResult.sms?.failed > 0 ? 'warning' : 'info'}
          showIcon
          closable
          onClose={() => setSendResult(null)}
          message={
            <Space size={16} wrap>
              <span>
                {sendResult.emailError
                  ? `邮件发送失败：${sendResult.emailError}`
                  : `邮件：成功 ${sendResult.sentCount}，失败 ${sendResult.failedCount}，跳过 ${sendResult.skippedCount}`}
              </span>
              <span>{smsSummary(sendResult.sms)}</span>
              {sendResult.sms?.results?.length > 0 && (
                <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setSmsResult(sendResult.sms)}>
                  短信明细
                </Button>
              )}
            </Space>
          }
          description={
            sendResult.failedCount > 0 && (
              <div>
                <strong>失败详情：</strong>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {sendResult.details?.failed?.map((f, i) => (
                    <li key={i}>{f.touristName}: {f.error}</li>
                  ))}
                </ul>
              </div>
            )
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Tourist List & Send */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <span style={{ fontWeight: 600 }}>游客通知书状态</span>
          <Button
            size="small"
            icon={<LinkOutlined />}
            onClick={generateLinks}
            loading={generating}
          >
            重新生成链接
          </Button>
        </Space>
        <Space>
          <Button
            icon={<MessageOutlined />}
            onClick={handleBatchSms}
            loading={smsSending}
            disabled={selectedTouristIds.length === 0 || sending}
          >
            仅发送短信 {selectedTouristIds.length > 0 && `(${selectedTouristIds.length}人)`}
          </Button>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleBatchSend}
            disabled={selectedTouristIds.length === 0}
            loading={sending || smsSending}
          >
            批量发送 {selectedTouristIds.length > 0 && `(${selectedTouristIds.length}人)`}
          </Button>
        </Space>
      </div>

      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={touristDetails}
        rowKey="id"
        size="small"
        scroll={{ x: 1100 }}
        pagination={false}
      />

      {/* 短信发送结果 */}
      <Modal
        open={!!smsResult}
        title="短信发送结果"
        width={640}
        onCancel={() => setSmsResult(null)}
        footer={<Button type="primary" onClick={() => setSmsResult(null)}>关闭</Button>}
      >
        {smsResult && (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}><Statistic title="成功" value={smsResult.sent} valueStyle={{ color: '#52c41a' }} /></Col>
              <Col span={8}><Statistic title="失败" value={smsResult.failed} valueStyle={{ color: '#cf1322' }} /></Col>
              <Col span={8}><Statistic title="跳过" value={smsResult.skipped} valueStyle={{ color: '#999' }} /></Col>
            </Row>
            <Table
              size="small"
              rowKey={(r, i) => `${r.phone}-${i}`}
              pagination={false}
              dataSource={smsResult.results || []}
              columns={[
                { title: '号码', dataIndex: 'phone', width: 140, render: (v) => v || '-' },
                { title: '游客', dataIndex: 'touristNames', render: (v) => (v || []).join('、') || '-' },
                {
                  title: '结果',
                  dataIndex: 'status',
                  width: 80,
                  render: (v) => (
                    v === 'sent' ? <Tag color="success">成功</Tag>
                      : v === 'failed' ? <Tag color="error">失败</Tag>
                        : <Tag>跳过</Tag>
                  ),
                },
                { title: '原因', dataIndex: 'reason', width: 140, render: (v) => v || '-' },
              ]}
            />
          </>
        )}
      </Modal>

      {/* 批量发送进度框 */}
      <Modal
        open={!!batchProgress}
        closable={batchProgress?.status !== 'running'}
        maskClosable={false}
        keyboard={false}
        footer={batchProgress?.status === 'done' ? (
          <Button type="primary" onClick={() => setBatchProgress(null)}>关闭</Button>
        ) : null}
        title={batchProgress?.status === 'done' ? '邮件发送完成' : '正在发送邮件'}
        width={520}
      >
        {batchProgress && (
          <>
            <Progress
              percent={Math.round((batchProgress.current / Math.max(batchProgress.total || 1, 1)) * 100)}
              status={
                batchProgress.status === 'error' ? 'exception' :
                batchProgress.status === 'done' ? 'success' : 'active'
              }
            />
            <div style={{ margin: '16px 0', fontSize: 14, minHeight: 22 }}>
              {batchProgress.status === 'done'
                ? `已处理 ${batchProgress.current}/${batchProgress.total}`
                : <>正在发送 <b>{batchProgress.current}</b> / {batchProgress.total}：
                    <span style={{ color: '#1677ff' }}>{batchProgress.currentName || '...'}</span>
                  </>
              }
            </div>
            <Row gutter={16}>
              <Col span={8}><Statistic title="成功" value={batchProgress.sentCount} valueStyle={{ color: '#52c41a' }} /></Col>
              <Col span={8}><Statistic title="失败" value={batchProgress.failedCount} valueStyle={{ color: '#cf1322' }} /></Col>
              <Col span={8}><Statistic title="跳过" value={batchProgress.skippedCount} valueStyle={{ color: '#999' }} /></Col>
            </Row>
            {batchProgress.status === 'running' && (
              <div style={{ marginTop: 16, fontSize: 12, color: '#999' }}>
                邮件正在后台发送，关闭浏览器也不会中断。可稍后再查看结果。
              </div>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default StepPublishSend;
